// Language-specific URL utilities

export const supportedLanguages = ['en', 'es', 'fr', 'de', 'pt', 'it', 'ru', 'ja', 'zh', 'ar'] as const;
export type SupportedLanguage = typeof supportedLanguages[number];

// URL mapping for tools
export const toolUrlMap: Record<string, string> = {
  'pdfMerger': 'pdf-merger',
  'pdfCompression': 'pdf-compression',
  'imageConverter': 'image-converter',
  'compression': 'compression',
  'androidIosResizer': 'image-resizer',
  'imageCropper': 'image-cropper',
  'imageToPdf': 'image-to-pdf',
  'faviconGenerator': 'favicon-generator',
  'qrCodeGenerator': 'qr-generator',
  'barcodeGenerator': 'barcode-generator',
  'emailSignature': 'email-signature',
  'invoiceGenerator': 'invoice-generator',
  'countdownTimer': 'countdown-timer',
  'dateCalculator': 'date-calculator',
  'csvJsonConverter': 'csv-json-converter',
  'passwordGenerator': 'password-generator',
  'loremIpsum': 'lorem-ipsum',
  'baseConverter': 'base-converter',
  'distanceConverter': 'distance-converter',
  'weightConverter': 'weight-converter',
  'temperatureConverter': 'temperature-converter',
  'bmiCalculator': 'bmi-calculator',
  'interestCalculator': 'interest-calculator',
  'creditCardCalculator': 'credit-card-calculator',
  'about': 'about',
  'caseConverter': 'case-converter',
  'uuidGenerator': 'uuid-generator',
  'base64Encoder': 'base64-encoder',
  'jwtDecoder': 'jwt-decoder',
  'jsonToCSharp': 'json-to-csharp',
  'timezoneConverter': 'timezone-converter',
};

/**
 * Generate a language-specific URL for a tool
 * @param language - The language code
 * @param toolKey - The tool key from the translation file
 * @returns The language-specific URL
 */
export function getLanguageUrl(language: SupportedLanguage, toolKey: string): string {
  const urlPath = toolUrlMap[toolKey];
  if (!urlPath) {
    console.warn(`No URL mapping found for tool: ${toolKey}`);
    return `/${language}`;
  }
  return `/${language}/${urlPath}`;
}

/**
 * Get the current language from the URL path
 * @param pathname - The current pathname
 * @returns The language code or 'en' as default
 */
export function getLanguageFromUrl(pathname: string): SupportedLanguage {
  const pathSegments = pathname.split('/').filter(Boolean);
  const language = pathSegments[0] as SupportedLanguage;
  
  if (supportedLanguages.includes(language)) {
    return language;
  }
  
  return 'en';
}

/**
 * Get the tool key from the URL path
 * @param pathname - The current pathname
 * @returns The tool key or null if not found
 */
export function getToolKeyFromUrl(pathname: string): string | null {
  const pathSegments = pathname.split('/').filter(Boolean);
  
  if (pathSegments.length < 2) {
    return null;
  }
  
  const urlPath = pathSegments[1];
  
  // Find the tool key by matching the URL path
  for (const [toolKey, mappedPath] of Object.entries(toolUrlMap)) {
    if (mappedPath === urlPath) {
      return toolKey;
    }
  }
  
  return null;
}

/**
 * Generate all language URLs for a tool
 * @param toolKey - The tool key
 * @returns Object with language codes as keys and URLs as values
 */
export function getAllLanguageUrls(toolKey: string): Record<SupportedLanguage, string> {
  const urls: Record<SupportedLanguage, string> = {} as Record<SupportedLanguage, string>;
  
  supportedLanguages.forEach(lang => {
    urls[lang] = getLanguageUrl(lang, toolKey);
  });
  
  return urls;
}

